import {memo, useState, useRef} from 'react';
import type {ReactNode, DragEvent} from 'react';
import classNames from 'classnames';
import {useEventListener} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';

import styles from './FileUpload.scss';

export interface Props {
  children?: ReactNode;
  empty?: boolean;
  disabled?: boolean;
  onDrop?(files: FileList): void;
}

function FileUploadComponent({children, empty, disabled, onDrop}: Props) {
  const [i18n] = useI18n();
  const [dragging, setDragging] = useState(false);
  const [rect, setRect] = useState<ClientRect | null>(null);

  const fileUploadRef = useRef<HTMLDivElement>(null);

  useEventListener('dragover', preventWindowDragOver);

  const isOutsideFileUploadContainer = (xPos: number, yPos: number) => {
    return rect
      ? xPos >= rect.left + rect.width ||
          xPos <= rect.left ||
          yPos >= rect.top + rect.height ||
          yPos <= rect.top
      : false;
  };

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (fileUploadRef.current && disabled !== true) {
      setDragging(true);
      setRect(fileUploadRef.current.getBoundingClientRect());
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      isOutsideFileUploadContainer(event.clientX, event.clientY) &&
      disabled !== true
    ) {
      setDragging(false);
      setRect(null);
    }
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const dataTransferFiles = event.dataTransfer.files;
    if (disabled !== true) {
      setDragging(false);
      setRect(null);
      if (onDrop != null) {
        onDrop(dataTransferFiles);
      }
    }
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled !== true) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const className = classNames(
    styles.FileUpload,
    empty || dragging ? styles.hasNoContent : null,
  );

  return (
    <div
      ref={fileUploadRef}
      className={className}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={styles.Content}>{children}</div>
      <div className={styles.Overlay}>
        <p className={styles.OverlayText}>
          {i18n.translate('FileUpload.message')}
        </p>
      </div>
    </div>
  );
}

function preventWindowDragOver(event: Event) {
  const dragEvent = event as unknown as DragEvent;
  if (dragEvent.dataTransfer == null) {
    return;
  }

  dragEvent.preventDefault();
  dragEvent.stopPropagation();

  dragEvent.dataTransfer.dropEffect = 'none';
}

export const FileUpload = memo(FileUploadComponent);
